// src/pages/OrganizationPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import EditOrganizationModal from '../components/EditOrganizationModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { toast } from 'react-toastify';
import { formatUtcToLocal } from '../utils/dateUtils';

const OrganizationPage = () => {
    const { orgId } = useParams();
    const navigate = useNavigate();
    const { organization: authOrganization, setOrganization: setAuthOrganization, reloadUser } = useAuth();
    const { canUpdate, canDelete, loading: permissionsLoading } = usePermissions('organization');

    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

    const handleConfirmDelete = async () => {
        try {
            await apiClient.delete(`/organizations/${orgId}`);
            toast.success(`Организация "${organization.name}" успешно удалена.`);
            await reloadUser();
            navigate('/profile');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка при удалении организации.');
        } finally {
            setShowConfirmDeleteModal(false);
        }
    };

    const fetchOrganization = useCallback(() => {
        setLoading(true);
        apiClient.get(`/organizations/${orgId}`)
            .then(response => {
                setOrganization(response.data);
                if (authOrganization && authOrganization.id === response.data.id) {
                    setAuthOrganization(response.data);
                }
            })
            .catch(err => {
                setError('Ошибка загрузки данных организации.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [orgId, setAuthOrganization]);

    useEffect(() => {
        fetchOrganization();
    }, [fetchOrganization]);

    const formatBytesToMB = (bytes) => {
        if (!bytes || bytes === 0) return '0.00';
        const mb = bytes / (1024 * 1024);
        return mb.toFixed(2);
    };

    if (loading || permissionsLoading) {
        return <div className="text-center">Загрузка...</div>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    if (!organization) {
        return <div className="alert alert-warning">Организация не найдена.</div>;
    }

    return (
        <>
            <div className="container mt-4">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h2 className="h4 mb-0">Профиль организации</h2>
                                <div className="d-flex gap-2">
                                    {canUpdate && (
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowEditModal(true)}
                                            title="Редактировать"
                                        >
                                            <i className="fas fa-pencil-alt"></i>
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button 
                                            className="btn btn-danger btn-sm" 
                                            onClick={() => setShowConfirmDeleteModal(true)}
                                            title="Удалить"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-12">
                                        <p className="mb-2"><strong>Название:</strong> {organization.name}</p>
                                        <div className="mb-2">
                                            <strong>Описание:</strong>
                                            <p className="mt-1 text-muted">{organization.description || 'Описание не указано.'}</p>
                                        </div>
                                        <div className="mb-2">
                                            <strong>Статус фонотеки:</strong>
                                            <span className={`ms-2 badge ${organization.is_songs_library_public ? 'bg-success' : 'bg-secondary'}`}>
                                                {organization.is_songs_library_public ? 'Публичная' : 'Приватная'}
                                            </span>
                                        </div>
                                        <div className="mb-2">
                                            <strong>Объем загруженных файлов:</strong> {formatBytesToMB(organization.storage_used)} / {formatBytesToMB(organization.storage_limit_bytes)} МБ
                                        </div>
                                        {organization.next_event && (
                                            <div>
                                                <strong>Ближайшее событие:</strong>
                                                <p className="mt-1">
                                                    <Link to={`/events/${organization.next_event.id}`}>{organization.next_event.name}</Link>
                                                    <br />
                                                    <small className="text-muted">{formatUtcToLocal(organization.next_event.date)}</small>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <EditOrganizationModal 
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                organization={organization}
                onOrganizationUpdated={fetchOrganization}
            />

            <ConfirmationModal
                show={showConfirmDeleteModal}
                title="Подтвердить удаление организации"
                message={`Вы уверены, что хотите удалить организацию "${organization?.name}"? Это действие необратимо и приведет к удалению ВСЕХ связанных данных (событий, песен, ролей и т.д.).`}
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowConfirmDeleteModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
                variant="danger"
            />
        </>
    );
};

export default OrganizationPage;
